import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { aboutFeatures, aboutStats, faqItems } from '@/mocks/faq';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [feedbackText, setFeedbackText] = useState('');

  const handleFeedbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9slg2tlbrt1qcgaj5mg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFeedbackText('');
        setFormError('');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const breadcrumbItems = [{ label: '首页', path: '/' }, { label: '关于我们' }];

  const orgSchema = {
    name: '免费看片平台推荐',
    url: 'https://readdy.ai',
    description: '致力于为用户推荐安全合规的正版免费影视平台，涵盖电影、电视剧、纪录片、动漫、综艺等海量资源。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@example.com',
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 | 免费看片平台推荐"
        description="免费看片平台推荐致力于为用户筛选安全合规的正版免费影视平台。了解我们的品牌故事、核心价值和联系方式，获取最可靠的免费观影指南。"
        keywords="关于免费看片平台,免费看片推荐,品牌介绍,联系方式"
        canonicalUrl="https://readdy.ai/about"
      />
      <SchemaOrg type="Organization" data={orgSchema} />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="bg-background-100/60 pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="max-w-3xl">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">关于我们</h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                免费看片平台推荐是一个专注于正版免费影视平台评测与推荐的独立媒体。
                我们致力于帮助广大用户在海量信息中，找到真正安全、合规、高质量的免费观影渠道。
              </p>
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">品牌故事</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                在互联网信息爆炸的时代，寻找免费影视资源变得越来越困难。盗版网站充斥恶意广告，
                虚假平台盗取个人信息，"免费"二字常常伴随着安全隐患。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                我们团队的创始成员来自互联网安全和媒体行业，深感用户需要一个可靠的信息来源。
                2024年初，我们决定创建这个平台，用专业的知识和严谨的态度，为用户筛选和评测真正值得信赖的免费正版影视平台。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们的使命很简单：让每个人都能安全、放心地享受免费的高质量影视内容。
                从最初的几个平台评测，到如今覆盖电影、电视剧、纪录片、动漫、综艺五大品类的完整推荐体系，
                我们已经帮助超过100万用户发现了适合自己的免费观影平台。
              </p>
            </div>
            <div className="rounded-lg overflow-hidden h-72 lg:h-80">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20cozy%20office%20workspace%20with%20team%20collaboration%2C%20modern%20minimal%20design%2C%20soft%20natural%20light%2C%20creative%20atmosphere%2C%20comfortable%20working%20environment%2C%20wooden%20desk%20with%20laptops&width=800&height=600&seq=about-brand&orientation=landscape"
                alt="品牌故事"
                title="免费看片平台推荐 - 品牌故事"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="bg-background-100/60 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">核心价值</h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                我们的每一项工作都围绕这四个核心价值展开
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutFeatures.map((feature) => (
                <div key={feature.title} className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center group hover:border-primary-200 transition-all duration-300">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                    <i className={`${feature.icon} text-primary-500 text-2xl w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {aboutStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-heading text-3xl md:text-4xl font-bold text-primary-500 mb-1">{stat.value}</p>
                <p className="text-sm text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact & Feedback */}
        <section id="contact" className="bg-background-100/60 py-14 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">联系我们</h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                如果你有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div id="feedback" className="space-y-6">
                <h3 className="font-heading text-lg font-semibold text-foreground-900">联系方式</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-600">contact@freemovierank.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-chat-3-line text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">社交媒体</p>
                      <p className="text-sm text-foreground-600">微信公众号：免费看片推荐</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center shrink-0">
                      <i className="ri-time-line text-secondary-500 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">工作时间</p>
                      <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>

                {/* FAQ in About */}
                <div className="mt-8">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">常见问题</h3>
                  <div className="space-y-2">
                    {faqItems.slice(0, 4).map((item, i) => (
                      <details key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-4 cursor-pointer">
                        <summary className="text-sm font-medium text-foreground-900 list-none flex items-center justify-between">
                          {item.question}
                          <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center text-foreground-500"></i>
                        </summary>
                        <p className="text-xs text-foreground-600 mt-3 leading-relaxed">{item.answer}</p>
                      </details>
                    ))}
                  </div>
                </div>
              </div>

              {/* Feedback Form */}
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">用户反馈</h3>
                <form
                  id="feedback-form"
                  data-readdy-form=""
                  onSubmit={handleFeedbackSubmit}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm text-foreground-700 mb-1">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="w-full border border-background-200/70 rounded-md px-3 py-2 text-sm bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-700 mb-1">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full border border-background-200/70 rounded-md px-3 py-2 text-sm bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm text-foreground-700 mb-1">反馈内容</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      className="w-full border border-background-200/70 rounded-md px-3 py-2 text-sm bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入反馈内容（限500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{feedbackText.length}/500</p>
                  </div>
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  <button
                    type="submit"
                    className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    提交反馈
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">感谢你的反馈，我们会尽快处理！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}